import React, { useEffect, useState } from "react";
import { ShieldAlert, Mail, Phone, Globe, ChevronDown, ChevronUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const PREVIEW = 5;
const DAY_MS = 24 * 3600 * 1000;

const fmt = (iso) =>
  new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

const LeadRow = ({ l }) => {
  const isNew = Date.now() - new Date(l.created_at).getTime() < DAY_MS;
  const site = l.website.startsWith("http") ? l.website : `https://${l.website}`;
  return (
    <li className="py-3 border-t border-red-400/10 first:border-t-0" data-testid={`guide-lead-${l.id}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-semibold text-white">{l.first_name} {l.last_name}</span>
        {isNew && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-red-500 text-white">Nouveau</span>}
        <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-slate-800 text-slate-400">PDF {l.pdf_lang} · {l.language}</span>
        <span className="text-xs text-slate-500 ml-auto">{fmt(l.created_at)}</span>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-sm text-slate-300">
        <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-white"><Mail className="w-3.5 h-3.5" />{l.email}</a>
        <a href={`tel:${l.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1 hover:text-white"><Phone className="w-3.5 h-3.5" />{l.phone}</a>
        <a href={site} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-white"><Globe className="w-3.5 h-3.5" />{l.website}</a>
      </div>
      <p className="mt-1.5 text-sm text-slate-400 whitespace-pre-line break-words">{l.reason}</p>
    </li>
  );
};

export const GuideLeadsPanel = ({ pin, refreshKey }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/guide/leads?pin=${encodeURIComponent(pin)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => setData(null));
  }, [pin, refreshKey]);

  if (!data) return null;
  const shown = open ? data.leads : data.leads.slice(0, PREVIEW);
  const newCount = data.leads.filter((l) => Date.now() - new Date(l.created_at).getTime() < DAY_MS).length;

  return (
    <section className="mb-6 bg-red-500/5 border border-red-400/20 rounded-2xl p-4 sm:p-6" data-testid="dash-guide-leads">
      <div className="flex items-center gap-2 mb-3">
        <ShieldAlert className="w-5 h-5 text-red-400" />
        <h2 className="text-sm sm:text-base font-bold uppercase tracking-wider text-red-300">Demandes Google Ads</h2>
        {newCount > 0 && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-500 text-white" data-testid="dash-guide-new-count">{newCount} nouv. 24h</span>}
        <span className="text-xs text-slate-500 ml-auto" data-testid="dash-guide-total">{data.total} au total</span>
      </div>
      {data.leads.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="dash-guide-empty">Aucune demande pour le moment.</p>
      ) : (
        <ul>{shown.map((l) => <LeadRow key={l.id} l={l} />)}</ul>
      )}
      {data.leads.length > PREVIEW && (
        <button onClick={() => setOpen(!open)} data-testid="dash-guide-toggle"
          className="mt-3 inline-flex items-center gap-1 text-sm text-red-300 hover:text-white transition-colors">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {open ? "Réduire" : `Voir les ${data.leads.length}`}
        </button>
      )}
    </section>
  );
};
